import { aiHeroData } from "@/mocks/aiSolutionsData";
import AnimatedSection from "@/components/AnimatedSection";

function FloatingParticle({ className, delay = 0 }: { className: string; delay?: number }) {
  return (
    <div
      className={`absolute w-1.5 h-1.5 rounded-full bg-accent-400/60 ${className}`}
      style={{ animation: `pulse-dot 3s ease-in-out ${delay}s infinite` }}
    />
  );
}

export default function AiHeroSection() {
  return (
    <section className="relative overflow-hidden bg-hero-gradient text-background-50 pt-28 md:pt-36 pb-16 md:pb-24">
      {/* Background visuals */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none grid-pattern" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-500/10 rounded-full blur-3xl" />
      <FloatingParticle className="top-[25%] left-[12%]" delay={0} />
      <FloatingParticle className="top-[30%] right-[18%]" delay={1} />
      <FloatingParticle className="top-[65%] left-[20%]" delay={2} />
      <FloatingParticle className="top-[55%] right-[25%]" delay={0.5} />

      <div className="container-astro relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left text */}
          <div>
            <AnimatedSection>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-500/30 bg-background-900/30 backdrop-blur-sm text-xs font-medium text-background-300">
                <span className="w-4 h-4 flex items-center justify-center text-accent-400">
                  <i className="ri-sparkling-2-line" />
                </span>
                {aiHeroData.badge}
              </span>
            </AnimatedSection>

            <AnimatedSection delay={120}>
              <h1 className="mt-6 text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight tracking-tight font-heading text-background-50">
                AI Solutions Built for{" "}
                <span className="gradient-text">Real Business Impact</span>
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={240}>
              <p className="mt-6 text-base md:text-lg text-background-300 leading-relaxed max-w-xl">
                {aiHeroData.subtext}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={360}>
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/30 whitespace-nowrap btn-arrow-hover group"
                >
                  {aiHeroData.primaryCTA}
                  <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href="#ai-services"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-background-400/40 text-background-100 text-sm font-semibold rounded-xl hover:bg-background-50/10 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group"
                >
                  {aiHeroData.secondaryCTA}
                  <i className="ri-arrow-down-line btn-arrow transition-transform duration-300 group-hover:translate-y-1" />
                </a>
              </div>
            </AnimatedSection>
          </div>

          {/* Right visual */}
          <AnimatedSection delay={200} direction="left">
            <div className="relative">
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-background-500/20 shadow-2xl shadow-primary-950/40">
                <img
                  src={aiHeroData.image}
                  alt="AI Solutions visual"
                  className="w-full h-64 md:h-80 lg:h-[420px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 via-transparent to-transparent" />
              </div>

              {/* Floating stat cards */}
              <div className="absolute -bottom-5 left-4 md:left-6 right-4 md:right-6 grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                {aiHeroData.statCards.map((card) => (
                  <div
                    key={card.label}
                    className="rounded-xl border border-background-500/20 bg-background-900/80 backdrop-blur-md px-3 py-3 text-center card-lift"
                  >
                    <p className="text-base md:text-lg font-extrabold font-heading gradient-text">
                      {card.value}
                    </p>
                    <p className="text-[11px] md:text-xs text-background-400 mt-0.5 leading-tight">
                      {card.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}